export default {

	data() {
		return {
			quizActive: true,
			activeQuestion: null,
			answerResponse: null,
			activeQuestionAnswered: null,
			openToStats: false,
			autoOpenTimeout: null,
			autoOpenCookieKey: null
		}
	},

	computed: {
		shouldShowQuiz() {
			if (!this.quizzes || this.quizzes.length === 0 || !this.quizzes[0]) {
				return false;
			}
			
			const quiz = this.quizzes[0];
			const now = new Date();
			
			// Don't show if before advertise date
			if (quiz.advertiseDateTime) {
				const advertiseDate = new Date(quiz.advertiseDateTime);
				if (now < advertiseDate) {
					return false;
				}
			}
			
			// Don't show if after closed until date
			if (quiz.showClosedUntilDateTime) {
				const showClosedUntil = new Date(quiz.showClosedUntilDateTime);
				if (now > showClosedUntil) {
					return false;
				}
			}
			
			return true;
		}
	},

	methods: 
	{
		isQuestionToday(question) {
			if (!question || !question.startDateTime || !question.endDateTime) {
				return false;
			}
			
			const now = new Date();
			const startDate = new Date(question.startDateTime);
			const endDate = new Date(question.endDateTime);
			
			return now >= startDate && now <= endDate;
		},

		loadQuizData() {
			this.activeQuestionAnswered = null;

			// Test mode: load from imported JSON instead of API
			if (this.quizTestMode && this.quizTestData) {
				const quiz = JSON.parse(JSON.stringify(this.quizTestData));
				// Override the selected question's time window to "now"
				const questionIndex = (this.quizTestQuestionNumber || 1) - 1;
				const question = quiz.questions[questionIndex];
				if (question) {
					const now = new Date();
					question.startDateTime = new Date(now.getTime() - 3600000).toISOString();
					question.endDateTime = new Date(now.getTime() + 3600000).toISOString();
				}
				// Override quiz-level dates so shouldShowQuiz and isQuizActive return true
				quiz.advertiseDateTime = new Date(Date.now() - 86400000).toISOString();
				quiz.startDateTime = new Date(Date.now() - 3600000).toISOString();
				quiz.endDateTime = new Date(Date.now() + 3600000).toISOString();
				quiz.showClosedUntilDateTime = new Date(Date.now() + 86400000).toISOString();

				this.quizzes = [quiz];
				this.activeQuestion = question;
				this.quizPoints = { answered: 0, correct: 0, total: 0, streakMeter: 0, streakMeterInterval: quiz.streakMeterInterval };
				this.quizAnswers = [];
				this.activeQuestionAnswered = false;
				return;
			}

			this._getQuizzes(true, false, false, () => {
				if (this.quizzes.length === 0) {
					return;
				}
				this.activeQuestion = this.quizzes[0]?.questions.find(q => this.isQuestionToday(q));
				this._getQuizPoints(this.quizzes[0]?.id, () => {
					this._getQuizAnswers(this.quizzes[0]?.id, () => {
						this.activeQuestionAnswered = false;
						if (this.activeQuestion) {
							const thisQuestionAnswered = this.quizAnswers.find(a => a.question.id === this.activeQuestion.id);
							this.activeQuestionAnswered = thisQuestionAnswered && thisQuestionAnswered.selectedAnswer != null;

						// Auto-open drawer if quiz is active, question not answered, and not auto-opened before for this question
						const autoOpenCookieKey = `quiz_auto_opened_${this.activeQuestion.id}`;
						const hasAutoOpened = this.$cookies.get(autoOpenCookieKey);

						if (!this.activeQuestionAnswered && this.shouldShowQuiz && !hasAutoOpened) {
							// Clean up old quiz auto-open cookies before setting new one
							const allCookies = this.$cookies.keys();
							allCookies.forEach(cookieKey => {
								if (cookieKey.startsWith('quiz_auto_opened_') && cookieKey !== autoOpenCookieKey) {
									this.$cookies.remove(cookieKey);
								}
							});

							// Store cookie key for potential cleanup on navigation
							this.autoOpenCookieKey = autoOpenCookieKey;

							// Delay auto-open by 3 seconds
							this.autoOpenTimeout = setTimeout(() => {
									// Check if component still exists and refs are available
									if (this.$refs && this.$refs.quizPlayDrawer) {
										// Set cookie to prevent auto-opening again for this question (expires in 24 hours)
										this.$cookies.set(autoOpenCookieKey, 'true', '1d');
										this.autoOpenCookieKey = null; // Clear the key since we've successfully opened

										this.$nextTick(() => {
											this.handleAnswerClick();
										});
									}
								}, 1000);
							}
						}
					});
				});
			});
		},

		handleRulesClick() {
			// Cancel auto-open if user manually opens drawer
			if (this.autoOpenTimeout) {
				clearTimeout(this.autoOpenTimeout);
				this.autoOpenTimeout = null;
				this.autoOpenCookieKey = null;
			}
			this.$refs.quizRulesDrawer.openDrawer();
		},

		handleAnswerClick() {
			// Cancel auto-open if user manually opens drawer
			if (this.autoOpenTimeout) {
				clearTimeout(this.autoOpenTimeout);
				this.autoOpenTimeout = null;
				this.autoOpenCookieKey = null;
			}
			this.openToStats = false;
			this.$nextTick(() => {
				this.$refs.quizPlayDrawer.openDrawer();
			});
		},

		handleShowStatsClick() {
			// Cancel auto-open if user manually opens drawer
			if (this.autoOpenTimeout) {
				clearTimeout(this.autoOpenTimeout);
				this.autoOpenTimeout = null;
				this.autoOpenCookieKey = null;
			}
			this.openToStats = true;
			this.$nextTick(() => {
				this.$refs.quizPlayDrawer.openDrawer();
			});
		},		
		
		handleHome() {
			// Ensure MenuBar is shown when returning to home
			document.body.classList.remove('hide-nav-bar');

			// Reset to null to hide the card during reload
			this.activeQuestionAnswered = null;

			// Test mode: just re-find the active question without API calls
			if (this.quizTestMode) {
				this.activeQuestion = this.quizzes[0]?.questions.find(q => this.isQuestionToday(q));
				this.activeQuestionAnswered = true;
				return;
			}

			this._getQuizzes(true, false, false, () => {
				this.activeQuestion = this.quizzes[0]?.questions.find(q => this.isQuestionToday(q));
				this._getQuizPoints(this.quizzes[0].id);
				this._getQuizAnswers(this.quizzes[0].id, () => {
					if (this.activeQuestion) {
						const thisQuestionAnswered = this.quizAnswers.find(a => a.question.id === this.activeQuestion.id);
						this.activeQuestionAnswered = thisQuestionAnswered && thisQuestionAnswered.selectedAnswer != null;
					} else {
						// No active question means quiz is finished
						this.activeQuestionAnswered = true;
					}
				});
			});
		},

		handleSubmitAnswer(quiz, answer) {
			// Clear previous answer response to prevent showing old data
			this.answerResponse = null;

			const question = quiz.questions.find(q => this.isQuestionToday(q));

			// Test mode: simulate response locally
			if (this.quizTestMode) {
				this.answerResponse = {
					isCorrect: answer.isCorrect,
					answerDescription: question.answerDescription,
					answerExample: question.answerExample,
					percentageCorrectCustomers: 65,
					points: {
						answered: (this.quizPoints.answered || 0) + 1,
						correct: (this.quizPoints.correct || 0) + (answer.isCorrect ? 1 : 0),
						total: (this.quizPoints.total || 0) + (answer.isCorrect ? question.correctPoints : question.incorrectPoints),
						streakMeter: (this.quizPoints.streakMeter || 0) + 1
					}
				};
				return;
			}

			this._postQuizAnswer(quiz.id, question.id, answer.id, (response) => {
				this._clearQuizCache(quiz.id);
				this.answerResponse = response;
			});
		},	
		handleTermsClick() {
			this.$refs.quizTermsDrawer.openDrawer();
		},
	
		handleNotificationsChange(enabled) {
			// Initialize preferences if they don't exist
			if (!this.customer.preferences) {
				this.customer.preferences = { 
					dailyCurrentBalanceNotifications: false, 
					dailyCreditBalanceNotifications: false, 
					quizNotifications: false
				};
			}
			
			// Update the quiz notifications preference
			this.customer.preferences.quizNotifications = enabled;
			
			// Save preferences using the Services mixin method
			this._savePreferences(() => {
				console.log('Quiz notifications preference saved:', enabled);
			});
		},

		handleQuizDrawerOpened() {
			document.body.classList.add('hide-nav-bar');
		},
		
		handleQuizDrawerClosed() {
			document.body.classList.remove('hide-nav-bar');
		}

	},

	beforeUnmount() {
		// Ensure MenuBar is shown when component is unmounted (handles Android back button)
		document.body.classList.remove('hide-nav-bar');

		// Cancel auto-open timeout if user navigates away
		if (this.autoOpenTimeout) {
			clearTimeout(this.autoOpenTimeout);
			this.autoOpenTimeout = null;
		}
		
		// Remove the auto-open cookie if user navigates away before drawer opens
		if (this.autoOpenCookieKey) {
			this.$cookies.remove(this.autoOpenCookieKey);
			this.autoOpenCookieKey = null;
		}
	}
}
